import { useState, useEffect, useCallback } from 'react';
import Nav from '../components/Nav';
import { getStoredToken, getOrgId, getUserEmail } from '../auth/cognito';
import { OrgUser, inviteUser } from '../api/admin';

const API_URL = 'https://0emko5cw6f.execute-api.ca-central-1.amazonaws.com/prod';

interface OrgPlan {
  orgId: string;
  name: string;
  domain: string;
  plan: string;
  seats: number;
  seatsUsed: number;
  status: string;
  billingEmail: string;
  trialEndsAt?: string;
}

async function fetchOrgPlan(): Promise<OrgPlan | null> {
  const token = getStoredToken();
  try {
    const res = await fetch(`${API_URL}/org/plan`, {
      headers: { Authorization: token || '' },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

async function fetchOrgUsers(): Promise<OrgUser[]> {
  const token = getStoredToken();
  try {
    const res = await fetch(`${API_URL}/org/users`, {
      headers: { Authorization: token || '' },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.users || [];
  } catch { return []; }
}

async function removeOrgMember(userId: string): Promise<{ success?: boolean; error?: string }> {
  const token = getStoredToken();
  try {
    const res = await fetch(`${API_URL}/org/users/${userId}`, {
      method: 'DELETE',
      headers: { Authorization: token || '' },
    });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

// ── helpers ─────────────────────────────────────────────────────────────────

function planColor(plan: string) {
  if (plan === 'enterprise') return '#a78bfa';
  if (plan === 'professional') return '#60a5fa';
  return '#94a3b8';
}

function statusColor(status: string) {
  if (status === 'active') return '#22c55e';
  if (status === 'invited') return '#f59e0b';
  if (status === 'inactive') return '#6b7280';
  return '#94a3b8';
}

function inp(extra?: React.CSSProperties): React.CSSProperties {
  return {
    width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    color: '#fff', outline: 'none', boxSizing: 'border-box' as const,
    ...extra,
  };
}

function SeatMeter({ used, total }: { used: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const barColor = pct >= 90 ? '#ef4444' : pct >= 75 ? '#f59e0b' : '#22c55e';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
        <span>{used} / {total} seats used</span>
        <span>{pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

// ── InviteModal ──────────────────────────────────────────────────────────────

function InviteModal({ orgId, onClose, onSuccess }: { orgId: string; onClose: () => void; onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !firstName.trim() || !lastName.trim()) { setErr('All fields required.'); return; }
    setLoading(true); setErr('');
    const result = await inviteUser(email.trim(), firstName.trim(), lastName.trim(), orgId);
    setLoading(false);
    if (result.error) { setErr(result.error); return; }
    onSuccess();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        background: 'linear-gradient(135deg,#0f172a,#1e1b4b)', border: '1px solid rgba(99,102,241,0.3)',
        borderRadius: 20, padding: '36px 32px', width: '100%', maxWidth: 440,
      }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: '#fff' }}>Invite Member</h3>
        <form onSubmit={submit}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: 5 }}>First name</label>
              <input style={inp()} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Jane" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: 5 }}>Last name</label>
              <input style={inp()} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Smith" />
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: 5 }}>Email address</label>
            <input style={inp()} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@company.com" />
          </div>
          {err && <div style={{ fontSize: 12, color: '#fca5a5', marginBottom: 14 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{
              flex: 1, padding: '10px 0', borderRadius: 10, fontSize: 13, cursor: 'pointer',
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)',
            }}>Cancel</button>
            <button type="submit" disabled={loading} style={{
              flex: 2, padding: '10px 0', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: loading ? 'rgba(99,102,241,0.4)' : 'rgba(99,102,241,0.85)',
              border: '1px solid rgba(99,102,241,0.5)', color: '#fff',
            }}>{loading ? 'Sending…' : 'Send Invite'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── main component ───────────────────────────────────────────────────────────

export default function EnterpriseOrgAdminDashboard() {
  const orgId = getOrgId();
  const myEmail = getUserEmail();

  const [org, setOrg] = useState<OrgPlan | null>(null);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeErr, setRemoveErr] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [planData, usersData] = await Promise.all([fetchOrgPlan(), fetchOrgUsers()]);
    setOrg(planData);
    setUsers(usersData);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleRemove(userId: string, email: string) {
    if (!window.confirm(`Remove ${email} from the organisation?`)) return;
    setRemovingId(userId); setRemoveErr('');
    const result = await removeOrgMember(userId);
    setRemovingId(null);
    if (result.error) { setRemoveErr(result.error); return; }
    setUsers(u => u.filter(x => x.userId !== userId));
  }

  const filtered = users.filter(u =>
    !search || u.email.toLowerCase().includes(search.toLowerCase()) ||
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  const active = users.filter(u => u.status === 'active').length;
  const invited = users.filter(u => u.status === 'invited').length;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0a0a1a,#0d1b3e,#050d1f)', fontFamily: "'Segoe UI',system-ui,sans-serif", color: '#fff' }}>
      <Nav />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#fff', margin: 0 }}>
            Organisation Management
          </h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
            Enterprise · {org?.name ?? orgId ?? 'Your Organisation'}
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>Loading…</div>
        ) : (
          <>
            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16, marginBottom: 28 }}>
              {/* Plan */}
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '20px 22px' }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Plan</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: planColor(org?.plan ?? ''), textTransform: 'capitalize' }}>
                  {org?.plan ?? '—'}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                  Status: <span style={{ color: statusColor(org?.status ?? '') }}>{org?.status ?? '—'}</span>
                </div>
              </div>

              {/* Seats */}
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '20px 22px' }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Seats</div>
                <SeatMeter used={org?.seatsUsed ?? users.length} total={org?.seats ?? 0} />
                {org && org.seatsUsed >= org.seats && (
                  <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 8 }}>
                    Seat limit reached — contact your admin to purchase more.
                  </div>
                )}
              </div>

              {/* Members */}
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '20px 22px' }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Members</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{users.length}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                  {active} active · {invited} invited
                </div>
              </div>

              {/* Domain */}
              {org?.domain && (
                <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '20px 22px' }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Domain</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#a5b4fc' }}>{org.domain}</div>
                  {org.trialEndsAt && (
                    <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>
                      Trial ends {new Date(org.trialEndsAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Members table */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Members</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    style={{ ...inp({ width: 220, padding: '7px 12px' }) }}
                    placeholder="Search members…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                  <button
                    onClick={() => setShowInvite(true)}
                    disabled={org ? users.length >= org.seats : false}
                    style={{
                      padding: '7px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      cursor: (org && users.length >= org.seats) ? 'not-allowed' : 'pointer',
                      background: (org && users.length >= org.seats) ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.85)',
                      border: '1px solid rgba(99,102,241,0.5)', color: '#fff',
                      whiteSpace: 'nowrap' as const,
                    }}
                  >
                    + Invite Member
                  </button>
                </div>
              </div>

              {removeErr && (
                <div style={{ padding: '10px 24px', background: 'rgba(239,68,68,0.1)', fontSize: 13, color: '#fca5a5' }}>{removeErr}</div>
              )}

              {filtered.length === 0 ? (
                <div style={{ padding: '48px 24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
                  {search ? 'No members match your search.' : 'No members yet. Invite someone to get started.'}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {['Name', 'Email', 'Role', 'Status', 'Last Active', ''].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u => (
                      <tr key={u.userId} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#fff', fontWeight: 500 }}>
                          {u.firstName} {u.lastName}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>{u.email}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            fontSize: 11, padding: '3px 8px', borderRadius: 6,
                            background: u.role === 'org_admin' ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.06)',
                            color: u.role === 'org_admin' ? '#c4b5fd' : 'rgba(255,255,255,0.5)',
                          }}>
                            {u.role === 'org_admin' ? 'OrgAdmin' : 'User'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: 'rgba(0,0,0,0.2)', color: statusColor(u.status) }}>
                            {u.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                          {u.lastActive ? new Date(u.lastActive).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          {u.email !== myEmail && (
                            <button
                              onClick={() => handleRemove(u.userId, u.email)}
                              disabled={removingId === u.userId}
                              style={{
                                fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                                color: '#fca5a5',
                              }}
                            >
                              {removingId === u.userId ? '…' : 'Remove'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {showInvite && orgId && (
        <InviteModal
          orgId={orgId}
          onClose={() => setShowInvite(false)}
          onSuccess={() => { setShowInvite(false); load(); }}
        />
      )}
    </div>
  );
}
